<template>
  <aside
    class="app-sidebar"
    :class="{ 'app-sidebar--collapsed': isCollapsed, 'app-sidebar--mobile-open': mobileOpen }"
  >
    <!-- Brand -->
    <div class="sidebar-brand">
      <img class="sidebar-brand__logo" :src="logoSrc" alt="VINPROP CRM" />
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <!-- Карта разделов ещё едет: скелетон вместо пустого места, иначе меню
           «мигает» отсутствием пунктов и это читается как потеря доступа. -->
      <div v-if="isNavLoading" class="nav-section" aria-hidden="true">
        <AppSkeleton v-for="n in 8" :key="n" class="nav-skeleton" variant="rounded" :height="40" />
      </div>

      <template v-else>
        <div class="nav-section" v-for="section in mainNavSections" :key="section.id">
          <Transition name="fade">
            <h3 v-if="!isCollapsed && section.title" class="nav-section-title">
              {{ section.title }}
            </h3>
          </Transition>

          <SidebarNavItem
            v-for="item in section.items"
            :key="item.path ?? item.label"
            :item="item"
            :collapsed="isCollapsed"
            :active-path="activePath"
            :link-id="isSettingsAnchor(item) ? 'settings-nav-link' : undefined"
            @expand-sidebar="expandSidebar"
            @navigate="isSettingsAnchor(item) ? handleSettingsClick() : undefined"
          />
        </div>
      </template>
    </nav>

    <!-- Footer -->
    <div class="sidebar-footer">
      <div class="sidebar-footer__divider" />

      <!-- «Настройки» переехали в основное меню (макет 737:2): в подвале
           остался только переключатель темы. -->

      <!-- Theme toggle -->
      <div class="theme-toggle">
        <button
          type="button"
          class="theme-toggle__btn"
          v-tooltip="isCollapsed ? themeLabel : undefined"
          @click="toggleTheme"
        >
          <SunIcon v-if="!isDark" :size="19" />
          <MoonIcon v-else :size="19" />
          <Transition name="slide-fade">
            <span v-if="!isCollapsed" class="theme-toggle__label">{{ themeLabel }}</span>
          </Transition>
        </button>
        <AppSwitch
          v-if="!isCollapsed"
          :model-value="isDark"
          size="small"
          :aria-label="themeLabel"
          @update:model-value="toggleTheme"
        />
      </div>

      <!-- Collapse -->
      <button
        type="button"
        class="sidebar-collapse"
        :aria-label="t('common.actions.collapseSidebar')"
        @click="toggleCollapse"
      >
        <ChevronLeftIcon v-if="!isCollapsed" :size="18" />
        <ChevronRightIcon v-else :size="18" />
      </button>
    </div>

    <!-- Comic-style Speech Bubble (Teleported) -->
    <Teleport to="body">
      <Transition name="bounce-in">
        <div
          v-if="showLanguageHint && !isCollapsed && bubblePosition"
          class="language-hint-bubble"
          :style="bubblePosition"
          @click="dismissHint"
        >
          <div class="bubble-content">
            <span class="bubble-emoji">🌐</span>
            <p class="bubble-text">{{ $t('common.hints.languageSettings') }}</p>
          </div>
          <div class="bubble-arrow"></div>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AppSkeleton, AppSwitch, useTheme } from '@vinprop/design-system'
import SidebarNavItem from './SidebarNavItem.vue'
import { buildSidebarNav, resolveActivePath, type NavItem } from './sidebarNav'
import { SECTION_REGISTRY } from '@/features/auth/sections/registry'
import { useSectionsStore } from '@/features/auth/stores/sections.store'
import { useCompanyVerificationsStore } from '@/features/company-verifications/stores/companyVerifications.store'
import { useCommunicationsBadgesStore } from '@/features/communications/stores/communicationsBadges.store'
import vinpropLogoBlue from '@/assets/vinprop-crm-logo.svg'
import vinpropLogoWhite from '@/assets/vinprop-crm-logo-white.svg'
import { MoonIcon, SunIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-vue-next'

export type { NavItem, NavSection } from './sidebarNav'

interface Props {
  collapsed?: boolean
  /** DEV-2232: на <900px сайдбар выехал поверх контента */
  mobileOpen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  mobileOpen: false,
})

const emit = defineEmits<{
  'update:collapsed': [value: boolean]
}>()

const route = useRoute()
const { t } = useI18n()
const { isDark, toggleTheme } = useTheme()

// Состав меню целиком определяет карта разделов с сервера (DEV-1256).
const sectionsStore = useSectionsStore()

// Бейдж-призыв на «Модерация компаний»: счётчик очереди «Требуют действия» с лёгким опросом;
// просрочка из того же опроса красит бейдж в алерт (DEV-1165).
const companyVerificationsStore = useCompanyVerificationsStore()
const MODERATION_POLL_INTERVAL = 30_000
let moderationPollTimer: ReturnType<typeof setInterval> | undefined

// DEV-2826. Бейджи «Коммуникаций»: тот же шаг опроса, пока вкладка браузера видна.
// Доступ к подпунктам стор проверяет сам на каждом шаге — карта разделов может
// прийти позже монтирования сайдбара.
const communicationsBadges = useCommunicationsBadgesStore()
let communicationsPollTimer: ReturnType<typeof setInterval> | undefined
const pollCommunications = () => {
  if (document.visibilityState === 'visible') void communicationsBadges.refresh()
}

// Local state
const isCollapsed = ref(props.collapsed)

// Логотип меняется под тему (синий — для светлой, белый — для тёмной).
const logoSrc = computed(() => (isDark.value ? vinpropLogoWhite : vinpropLogoBlue))
const themeLabel = computed(() =>
  isDark.value ? t('common.labels.darkTheme') : t('common.labels.lightTheme'),
)

// Language hint state
const LANGUAGE_HINT_KEY = 'language-hint-dismissed'
const showLanguageHint = ref(!localStorage.getItem(LANGUAGE_HINT_KEY))
const bubblePosition = ref<Record<string, string> | null>(null)

const updateBubblePosition = () => {
  if (!showLanguageHint.value) {
    bubblePosition.value = null
    return
  }
  // DEV-2232: на мобильном пункт меню лежит за краем экрана, пузырь уезжал бы вместе с ним
  if (window.matchMedia('(max-width: 900px)').matches) {
    bubblePosition.value = null
    return
  }
  const el = document.getElementById('settings-nav-link')
  if (!el) {
    bubblePosition.value = null
    return
  }
  const rect = el.getBoundingClientRect()
  bubblePosition.value = {
    position: 'fixed',
    left: `${rect.right + 24}px`,
    top: `${rect.top + rect.height / 2}px`,
    transform: 'translateY(-50%)',
  }
}

onMounted(() => {
  setTimeout(() => {
    nextTick(updateBubblePosition)
  }, 100)
  window.addEventListener('resize', updateBubblePosition)
  window.addEventListener('scroll', updateBubblePosition, true)

  if (sectionsStore.hasSection('company_verifications')) {
    companyVerificationsStore.fetchPendingCount()
    moderationPollTimer = setInterval(
      () => companyVerificationsStore.fetchPendingCount(),
      MODERATION_POLL_INTERVAL,
    )
  }

  pollCommunications()
  communicationsPollTimer = setInterval(pollCommunications, MODERATION_POLL_INTERVAL)
  document.addEventListener('visibilitychange', pollCommunications)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateBubblePosition)
  window.removeEventListener('scroll', updateBubblePosition, true)
  if (moderationPollTimer) clearInterval(moderationPollTimer)
  if (communicationsPollTimer) clearInterval(communicationsPollTimer)
  document.removeEventListener('visibilitychange', pollCommunications)
})

watch([showLanguageHint, isCollapsed], () => {
  setTimeout(() => {
    nextTick(updateBubblePosition)
  }, 50)
})

// Структура меню — в sidebarNav.ts; здесь только реактивные входные данные.
const isNavLoading = computed(
  () => sectionsStore.status === 'idle' || sectionsStore.status === 'loading',
)

const nav = computed(() =>
  buildSidebarNav(sectionsStore.navigationKeys, t, {
    pendingVerifications: companyVerificationsStore.pendingCount,
    overdueVerifications: companyVerificationsStore.overdueCount,
    unreadChats: communicationsBadges.unreadChats,
    awaitingCallback: communicationsBadges.awaitingCallback,
    pendingSuggestions: communicationsBadges.pendingSuggestions,
  }),
)

const mainNavSections = computed(() => nav.value.sections)

/**
 * Якорь подсказки о смене языка: она указывает на пункт меню «Настройки».
 * Личные настройки живут подпунктом группы, поэтому цепляемся за её заголовок;
 * если группа схлопнулась в единственный пункт — за сам пункт.
 */
const SETTINGS_PATH = SECTION_REGISTRY.settings.path
const isSettingsAnchor = (item: NavItem): boolean =>
  item.path === SETTINGS_PATH || !!item.children?.some((c) => c.path === SETTINGS_PATH)

// Подсвечиваем ровно один пункт — самый специфичный для текущего маршрута.
const activePath = computed(() =>
  resolveActivePath(
    nav.value.sections.flatMap((s) => s.items),
    route.path,
  ),
)

const handleSettingsClick = () => {
  if (showLanguageHint.value) dismissHint()
}

const dismissHint = () => {
  showLanguageHint.value = false
  localStorage.setItem(LANGUAGE_HINT_KEY, 'true')
}

const setCollapsed = (value: boolean) => {
  isCollapsed.value = value
  emit('update:collapsed', value)
  localStorage.setItem('sidebar-collapsed', String(value))
}

const toggleCollapse = () => setCollapsed(!isCollapsed.value)
// Подпункты группы не видны в свёрнутом сайдбаре — раскрываем панель по клику.
const expandSidebar = () => setCollapsed(false)

watch(
  () => props.collapsed,
  (newVal) => {
    isCollapsed.value = newVal
  },
)

// Восстанавливаем состояние из localStorage.
// На мобильном брейкпоинте сохранённое значение игнорируем (DEV-2232): там
// сайдбар и так за экраном, а режим 80px обрезал бы подписи пунктов.
const savedCollapsed = localStorage.getItem('sidebar-collapsed')
if (savedCollapsed !== null && !window.matchMedia('(max-width: 900px)').matches) {
  isCollapsed.value = savedCollapsed === 'true'
}
</script>

<style scoped>
.app-sidebar {
  width: 280px;
  /* dvh, а не vh: на iOS низ панели уезжал под адресную строку (DEV-2232).
     Плашка вкладки просмотра (DEV-2399) отнимает свою высоту. */
  height: calc(100dvh - var(--impersonation-banner-height, 0px));
  background: var(--color-surface);
  border-right: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  padding: 20px 16px 18px;
  gap: 4px;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: sticky;
  top: var(--impersonation-banner-height, 0px);
  z-index: var(--z-index-sidebar, 40);
}

.app-sidebar--collapsed {
  width: 80px;
}

/* DEV-2232: на телефоне и узком планшете панель выезжает поверх контента */
@media (max-width: 900px) {
  .app-sidebar {
    position: fixed;
    top: var(--impersonation-banner-height, 0px);
    bottom: 0;
    left: 0;
    width: 280px;
    max-width: 82vw;
    transform: translateX(-100%);
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  }

  .app-sidebar--mobile-open {
    transform: translateX(0);
  }

  /* Свёрнутый режим 80px в выехавшей панели бессмысленен — кнопка только мешает */
  .sidebar-collapse {
    display: none;
  }
}

/* Выехавшая панель не должна ползать вместе с пальцем по странице */
@media (max-width: 900px) and (prefers-reduced-motion: reduce) {
  .app-sidebar {
    transition: none;
  }
}

/* Brand */
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 8px 8px;
  overflow: hidden;
}

.sidebar-brand__logo {
  height: 28px;
  width: auto;
  display: block;
  flex-shrink: 0;
}

.app-sidebar--collapsed .sidebar-brand {
  justify-content: center;
  padding: 4px 0 8px;
}

.app-sidebar--collapsed .sidebar-brand__logo {
  height: 30px;
  width: 30px;
  object-fit: cover;
  object-position: left center;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-skeleton {
  border-radius: 10px;
  opacity: 0.6;
}

.nav-section-title {
  font-size: 0.6875rem; /* 11px */
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-text-tertiary);
  padding: 16px 0 4px 12px;
  margin: 0;
}

/* Footer */
.sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sidebar-footer__divider {
  height: 1px;
  background: var(--color-border-light);
  margin: 6px 0;
}

.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 42px;
  padding: 0 10px 0 12px;
  border-radius: var(--radius-md, 10px);
}

.theme-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
}

.theme-toggle__btn:hover {
  color: var(--color-text-primary);
}

.theme-toggle__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  margin-top: 2px;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.sidebar-collapse:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

/* Collapsed state */
.app-sidebar--collapsed .theme-toggle {
  justify-content: center;
  padding: 0;
}

.app-sidebar--collapsed .theme-toggle__btn {
  flex: 0;
  justify-content: center;
}

/* Comic-style Speech Bubble */
.language-hint-bubble {
  background: #ffffff;
  border: 5px solid #000000;
  border-radius: 1.5rem;
  padding: 1.25rem 1.5rem;
  min-width: 240px;
  max-width: 320px;
  box-shadow:
    6px 6px 0 rgba(0, 0, 0, 0.2),
    0 0 0 2px #000000;
  /* DEV-2235: подсказка стояла на 9999 — вровень с модалкой и перекрывала её. */
  z-index: var(--z-index-floating, 9980);
  cursor: pointer;
  pointer-events: auto;
  animation: bubble-float 2s ease-in-out infinite;
  transition: all 0.2s;
}

.language-hint-bubble:hover {
  transform: translate(0, -50%) scale(1.05) !important;
  box-shadow:
    8px 8px 0 rgba(0, 0, 0, 0.2),
    0 0 0 2px #000000;
}

.bubble-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.bubble-emoji {
  font-size: 1.75rem;
  flex-shrink: 0;
  animation: emoji-wiggle 1s ease-in-out infinite;
}

.bubble-text {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #000000;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-transform: none;
}

@keyframes emoji-wiggle {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-5deg);
  }
  75% {
    transform: rotate(5deg);
  }
}

.bubble-arrow {
  position: absolute;
  left: -22px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 15px solid transparent;
  border-bottom: 15px solid transparent;
  border-right: 22px solid #000000;
}

.bubble-arrow::after {
  content: '';
  position: absolute;
  left: 6px;
  top: -11px;
  width: 0;
  height: 0;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-right: 16px solid #ffffff;
}

@keyframes bubble-float {
  0%,
  100% {
    transform: translate(0, -50%);
  }
  50% {
    transform: translate(4px, -50%);
  }
}

.bounce-in-enter-active {
  animation: bounce-in-anim 0.5s ease-out;
}

.bounce-in-leave-active {
  animation: bounce-out-anim 0.3s ease-in;
}

@keyframes bounce-in-anim {
  0% {
    opacity: 0;
    transform: translate(0, -50%) scale(0.3);
  }
  50% {
    transform: translate(0, -50%) scale(1.05);
  }
  70% {
    transform: translate(0, -50%) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translate(0, -50%) scale(1);
  }
}

@keyframes bounce-out-anim {
  0% {
    opacity: 1;
    transform: translate(0, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(0, -50%) scale(0.3);
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

/* Scrollbar */
.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: var(--color-border-light);
  border-radius: 2px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: var(--color-border-default);
}
</style>
